/**
 * VoiceControlBar — compact bottom control module.
 *
 * Idle: single centered "Start Conversation" pill.
 * Connecting: minimal "Connecting…" pill.
 * Live: compact glass pill with three actions:
 *     1. Text/keyboard toggle (secondary, left)
 *     2. Central mic button with controlled cyan glow (primary)
 *     3. End session button (right)
 *
 * Text composer opens ABOVE the pill via absolute positioning
 * anchored to the outer relative wrapper — center-aligned with the
 * pill at a matched ~520px width so it never overlaps controls.
 * Enter sends, Esc closes, focus ring in cyan.
 */

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, PhoneOff, Send, Keyboard, X, Radio } from "lucide-react";

export function VoiceControlBar({
    agentState,
    micMuted,
    audioMode = "live",
    audioModeReason = null,
    onStart,
    onEnd,
    onToggleMute,
    onSendText,
}) {
    const [text, setText] = useState("");
    const [textOpen, setTextOpen] = useState(false);
    const inputRef = useRef(null);

    const isIdle = agentState === "idle" || agentState === "error";
    const isConnecting = agentState === "connecting";
    const isTextOnly = audioMode === "text_only";
    const showComposer = (textOpen || isTextOnly) && !isIdle && !isConnecting;

    const submit = (e) => {
        e?.preventDefault?.();
        if (!text.trim()) return;
        onSendText?.(text);
        setText("");
    };

    const closeComposer = () => {
        setTextOpen(false);
        setText("");
    };

    // Focus the input when composer opens; handle Escape to close.
    useEffect(() => {
        if (!showComposer) return;
        inputRef.current?.focus?.();
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                closeComposer();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [showComposer]);

    return (
        <div
            data-testid="voice-control-bar"
            className="relative flex flex-col items-center"
        >
            {/* Composer — absolute-positioned ABOVE the pill so opening
                it never pushes the pill downward. We wrap the animated
                motion.form in a plain positioning div: Framer Motion
                writes its own `transform` on the animated node during
                enter/exit, which would clobber Tailwind's
                `-translate-x-1/2`. Keeping positioning on the OUTER div
                (unmanipulated by Framer) and animation on the INNER
                motion.form fixes the ~260px right-offset regression. */}
            <AnimatePresence>
                {showComposer && (
                    <div
                        key="composer-position"
                        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-[min(520px,80vw)]"
                    >
                        <motion.form
                            data-testid="text-composer"
                            onSubmit={submit}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 6 }}
                            transition={{ duration: 0.18, ease: "easeOut" }}
                            className="flex items-center gap-2 qoryn-panel rounded-2xl pl-4 pr-2 py-2 shadow-[0_10px_40px_-12px_rgba(0,229,255,0.25)]"
                            style={{
                                borderColor: "rgba(0,229,255,0.18)",
                            }}
                        >
                            <input
                                ref={inputRef}
                                data-testid="text-input"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Send text into the live session…"
                                aria-label="Type a message to Qoryn"
                                className="flex-1 bg-transparent border-none outline-none text-sm text-qoryn-text placeholder:text-qoryn-textMuted font-sans"
                            />
                            <span
                                className="hidden sm:inline font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted select-none"
                                aria-hidden="true"
                            >
                                enter ↵
                            </span>
                            <button
                                type="button"
                                data-testid="text-close-btn"
                                onClick={closeComposer}
                                aria-label="Close composer (Esc)"
                                title="Close (Esc)"
                                className="flex h-8 w-8 items-center justify-center rounded-full text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.06] transition-colors duration-150 qoryn-focus"
                            >
                                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                            </button>
                            <button
                                type="submit"
                                data-testid="send-text-btn"
                                aria-label="Send text"
                                disabled={!text.trim()}
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-qoryn-cyan/15 text-qoryn-cyan hover:bg-qoryn-cyan/25 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-150 qoryn-focus"
                            >
                                <Send className="h-3.5 w-3.5" strokeWidth={1.75} />
                            </button>
                        </motion.form>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
                {isIdle ? (
                    <motion.button
                        key="start"
                        data-testid="start-session-btn"
                        onClick={onStart}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="group relative flex items-center gap-2.5 rounded-full px-5 py-2.5 text-[13px] font-medium tracking-tight text-black bg-qoryn-cyan hover:bg-qoryn-cyanBright active:scale-[0.98] transition-[background-color,transform] duration-200 qoryn-focus qoryn-cyan-glow"
                    >
                        <Radio className="h-3.5 w-3.5" strokeWidth={2} />
                        <span>Start Conversation</span>
                    </motion.button>
                ) : isConnecting ? (
                    <motion.div
                        key="connecting"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="qoryn-panel rounded-full px-5 py-2.5 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim"
                        data-testid="dock-connecting"
                    >
                        Connecting…
                    </motion.div>
                ) : (
                    <motion.div
                        key="live"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="flex items-center gap-1.5 qoryn-panel rounded-full px-1.5 py-1.5"
                    >
                        {/* Text toggle */}
                        <button
                            data-testid="open-text-input-btn"
                            aria-label={textOpen ? "Close text input" : "Open text input"}
                            aria-pressed={textOpen}
                            onClick={() => setTextOpen((v) => !v)}
                            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors duration-200 qoryn-focus ${
                                textOpen
                                    ? "bg-qoryn-cyan/15 text-qoryn-cyan"
                                    : "text-qoryn-textDim hover:text-qoryn-text hover:bg-white/[0.04]"
                            }`}
                            title="Type instead"
                        >
                            <Keyboard className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>

                        {/* Central mic — primary action. In text-only
                            mode we swap it for a non-interactive
                            indicator so the Founder isn't tricked into
                            thinking a real mic is live. Muting a
                            synthetic silent track would be a no-op. */}
                        {isTextOnly ? (
                            <div
                                data-testid="text-only-indicator"
                                aria-label="Text-only mode — microphone unavailable"
                                title={
                                    audioModeReason
                                        ? `Text-only mode (${audioModeReason})`
                                        : "Text-only mode"
                                }
                                className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.04] text-qoryn-textDim border border-white/[0.08]"
                            >
                                <MicOff
                                    className="h-4 w-4"
                                    strokeWidth={1.75}
                                />
                            </div>
                        ) : (
                            <button
                                data-testid="toggle-mute-btn"
                                aria-label={
                                    micMuted
                                        ? "Unmute microphone"
                                        : "Mute microphone"
                                }
                                aria-pressed={micMuted}
                                onClick={onToggleMute}
                                className={`relative flex h-11 w-11 items-center justify-center rounded-full transition-[background-color,box-shadow] duration-200 qoryn-focus ${
                                    micMuted
                                        ? "bg-red-500/15 text-red-300"
                                        : "bg-qoryn-cyan/15 text-qoryn-cyan hover:bg-qoryn-cyan/20"
                                }`}
                                style={
                                    micMuted
                                        ? {}
                                        : {
                                              boxShadow:
                                                  "0 0 24px 0 rgba(0,229,255,0.35), inset 0 0 0 1px rgba(0,229,255,0.35)",
                                          }
                                }
                            >
                                {micMuted ? (
                                    <MicOff
                                        className="h-4 w-4"
                                        strokeWidth={1.75}
                                    />
                                ) : (
                                    <Mic
                                        className="h-4 w-4"
                                        strokeWidth={1.75}
                                    />
                                )}
                            </button>
                        )}

                        {/* End session */}
                        <button
                            data-testid="end-session-btn"
                            aria-label="End session"
                            onClick={onEnd}
                            className="flex h-9 w-9 items-center justify-center rounded-full text-qoryn-textDim hover:text-red-300 hover:bg-red-500/10 transition-colors duration-200 qoryn-focus"
                        >
                            <PhoneOff className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default VoiceControlBar;
